import { ButtonDock, miniButtonDockScrollBottomPad } from '@focuslee/shared-mini-app';
import { Box } from '@mui/material';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { allowedColors } from '../../helpers/color';
import { getButtonThemeColors } from '../../helpers/theme';
import { Page } from '../Page';
import { ColorCircle } from './ColorCircle';

interface ColorPickerSettingsProps {
    initialColor: string;
    isLoading?: boolean;
    onSave: (settings: { color: string }) => Promise<void>;
    onCancel: () => void;
}

export const ColorPickerSettings: FC<ColorPickerSettingsProps> = ({ initialColor, isLoading = false, onSave, onCancel }) => {
    const { t } = useTranslation();
    const [selectedColor, setSelectedColor] = useState<string>(initialColor);

    const handleSave = useCallback(async () => {
        await onSave({ color: selectedColor });
    }, [selectedColor, onSave]);

    return (
        <Page
            back={true}
            insetEnabled={false}
            edgeToEdge
            backClick={onCancel}
            footer={
                <ButtonDock
                    secondary={{ text: t('actions.cancel'), onClick: onCancel, navigation: 'back' }}
                    main={{
                        text: t('actions.save'),
                        onClick: handleSave,
                        loading: isLoading,
                        bgColor: getButtonThemeColors(true).backgroundColor,
                    }}
                />
            }
        >
            <Box sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        paddingTop:
                            'calc(var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px))',
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(6, 1fr)',
                            gap: '16px',
                            justifyItems: 'center',
                            paddingLeft: '10px',
                            paddingRight: '10px',
                            paddingTop: '10px',
                            paddingBottom: miniButtonDockScrollBottomPad,
                        }}
                    >
                        {allowedColors.map(code => (
                            <ColorCircle
                                key={code}
                                code={code}
                                selected={selectedColor === code}
                                onSelect={setSelectedColor}
                                sx={{ cursor: 'pointer' }}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>
        </Page>
    );
};
